import { useEffect } from "react";

const RELOAD_KEY = "chunk-reloaded-at";
const RELOAD_COOLDOWN_MS = 10_000;

/**
 * Clears app cache and reloads the page once when a chunk from an older
 * deployment can't be loaded. The cooldown prevents a reload loop when the
 * failure is not deployment related (e.g. the user is offline).
 * Returns false when a reload already happened within the cooldown.
 */
const reloadOnce = (): boolean => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable: reload anyway
  }

  caches
    .keys()
    .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.error("Failed to delete chunk", err);
    })
    .finally(() => location.reload());

  return true;
};

export const ChunkReloader = () => {
  useEffect(() => {
    const handleError = (e: ErrorEvent) => {
      const isChunkLoadError =
        e?.message?.includes("dynamically imported module") ||
        e?.message?.includes("ChunkLoadError");

      if (isChunkLoadError) reloadOnce();
    };

    // Fired by Vite when a lazy JS/CSS chunk fails to load ("Unable to preload CSS", "Importing a module script failed").
    const handlePreloadError = (e: Event) => {
      if (reloadOnce()) e.preventDefault();
    };

    window.addEventListener("error", handleError);
    window.addEventListener("vite:preloadError", handlePreloadError);
    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("vite:preloadError", handlePreloadError);
    };
  }, []);

  return null;
};
